import {
  ChatBotMessageTypes,
  OnboardingFlowStepStatus,
  OnboardingFlowStepTypes,
  OnboardingFlowType,
  Tool,
  useGetAllToolsQuery,
  useSendMessageToBotMutation,
  UserTask,
  UserTaskSource,
} from '__generated__/api-types-and-hooks'
import React, { useEffect, useState, useRef, useCallback, Dispatch, SetStateAction } from 'react'
import {
  AppDynamicChatMessage,
  FormComponent,
  TextComponent,
  TypingMessage,
  UIComponentProps,
} from 'components/AppOnboarding/AppChatbot/AppDynamicChatMessage/AppDynamicChatMessage.component'
import { useParams } from 'react-router-dom'
import { AppTypingIndicator } from 'components/AppOnboarding/AppChatbot/AppTypingIndicator/AppTypingIndicator.component'
import {
  audioMaxDuration,
  errorMessages,
} from 'components/AppOnboarding/AppChatbot/AppChatBotFlows/constants'
import { useInfiniteGetMessagesFromBotQuery } from '__generated__/infinite-api-types-and-hooks'
import { LoadingIndicator } from 'stream-chat-react'
import CircleNotchIcon from 'components/Common/SvgIcons/CircleNotchIcon'
import { useFlashParams } from '../../../../hooks/useFlashParams'
import { getTenantId } from 'utils/getTenantId'
import { AppPaperClipIcon } from 'components/Common/AppSvgIcons/AppPaperClipIcon'
import { AppButton } from 'components/Common/AppButton/AppButton.component'
import { AppSendMessageIcon } from 'components/Common/AppSvgIcons/AppSendMessageIcon'
import { AppPlayIcon } from 'components/Common/AppSvgIcons/AppPlayIcon'
import Avatar from 'appAssets/appImages/Avatar.png'
import { AppModalBox } from 'components/Common/AppModalBox/AppModalBox.component'
import { AppFileUploadField } from 'components/Common/AppFileUploadField/AppFileUploadField.component'
import moment from 'moment-timezone'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import { getRoleFromPath, helpDesk } from 'utils/helper'
import { findLast, findLastIndex } from 'lodash'
import redirectTo from 'utils/redirectTo'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
import { useForm } from 'react-hook-form'
import { ReactComponent as PenIcon } from 'assets/svgs/pen-icon.svg'
import { ReactComponent as CopyIcon } from 'assets/svgs/copy-icon.svg'
import { AppAudioRecorder } from 'components/Common/AppAudioRecorder/AppAudioRecorder.component'
import { extractTextForTTS, isAudioRecordingSupported } from 'utils/audioUtils'
import { AppVoicePlayback } from 'components/Common/AppVoicePlayback/AppVoicePlayback.component'
import { useVoicePlayback } from 'hooks/useVoicePlayback'
import copyToClipboard from 'utils/copyToClipboard'
import { AppTask } from 'components/AppKanbanBoard/AppTask/AppTask.component'
import { AppTaskCreationConfirmation } from 'components/AppKanbanBoard/AppTaskCreationConfirmation/AppTaskCreationConfirmation.component'
import AppCrossIcon from 'components/Common/AppSvgIcons/AppModalCrossIcon'
import { useGetBusinessProfile } from 'appHooks/useGetBusinessProfile'
import { AppToolTip } from 'components/Common/AppToolTip/AppToolTip.component'
import { SafeMarkdown } from 'components/Common/SafeMarkdown/SafeMarkdown.component'
import { ISmartGoal } from 'components/AppOnboarding/AppChatbot/AppDynamicChatMessage/AppSmartGoalCard/types'
import { useFeatureFlag } from 'hooks/userFeatureFlag'
import { clearUserTaskFormData } from 'utils/formPersistence'

interface OnboardingStepMessageResponse {
  stepType?: OnboardingFlowStepTypes
  stepStatus?: OnboardingFlowStepStatus
  content?: UIComponentProps
  progress?: number
}

export const AppGetToKnowYouRoom = ({
  initialMessage,
  sessionId,
  uploadFileAction,
  flowType,
  uploadedFileId,
  uploadFileLoading,
  logo,
  userInitial,
  refetchBusinessGoal,
  setProgress,
  progress,
  selectedGoalTemplate,
  onGoalTemplateUsed,
}: {
  initialMessage?: string
  sessionId?: string
  uploadedFileId: string[]
  uploadFileLoading: boolean
  flowType?: OnboardingFlowType
  logo?: string
  userInitial?: string
  refetchBusinessGoal: () => void
  setProgress: Dispatch<SetStateAction<number>>
  progress: number
  uploadFileAction: (options: { files: FileList; isMultiple: boolean; saveFile: boolean }) => void
  selectedGoalTemplate?: string
  onGoalTemplateUsed?: () => void
}) => {
  const [messages, setMessages] = useState<
    {
      sender: string
      text?: string
      content?: UIComponentProps
      stepType?: OnboardingFlowStepTypes
      stepStatus?: OnboardingFlowStepStatus
      createdAt?: string
      isEdited?: boolean | null
      isVoiceMessage?: boolean
      voiceDuration?: number
      isEditingMessage?: boolean
      editingOriginalMessageIndex?: number
      editingStepType?: OnboardingFlowStepTypes | null
      editingStepContent?: UIComponentProps | null
      editingMessageText?: string
      originalPromptingAssistantIndex?: number
      originalPromptingStepType?: OnboardingFlowStepTypes | null
      originalPromptingStepContent?: UIComponentProps | null
    }[]
  >([])
  const [queuedMessages, setQueuedMessages] = useState<OnboardingStepMessageResponse[]>([])
  // const [emojiClicked, setEmojiClicked] = useState(false)

  const [inputValue, setInputValue] = useState(initialMessage)
  const [editingTextareaValue, setEditingTextareaValue] = useState('')
  const [currentStep, setCurrentStep] = useState<{
    stepType?: OnboardingFlowStepTypes
    stepStatus?: OnboardingFlowStepStatus
  }>({})
  const [processing, setProcessing] = useState(false)
  const chatContainerRef = useRef<HTMLDivElement>(null)
  const [disableButton, setDisableButton] = useState(false)
  const [isUserScrolling, setIsUserScrolling] = useState(false)
  const [isUpload, setIsUpload] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<File[]>([])
  const [startUpload, setStartUpload] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)

  // Audio-related state
  const [audioSupported, setAudioSupported] = useState(false)
  const [showCreatePlayModal, setShowCreatePlayModal] = useState(false)
  const [showPlaySuccessModal, setShowPlaySuccessModal] = useState(false)
  const [showPlayErrorModal, setShowPlayErrorModal] = useState(false)
  const [createdUserTask, setCreatedUserTask] = useState<UserTask | null>(null)
  const [isPlayCreated, setIsPlayCreated] = useState(false)
  const { data: toolsData, refetch } = useGetAllToolsQuery(
    {
      tenantId: getTenantId(),
    },
    {
      refetchOnWindowFocus: false,
    }
  )
  const tools = (toolsData?.getAllTools?.data as Tool[]) || []
  const user = useSelector((rootState: RootState) => rootState.user.user)
  const showGetHelpButton = useFeatureFlag(user?.id || '', 'release-chatbot-get-help')
  const { clientId } = useParams()

  // Get assignee options and advisor data for play creation
  const { assigneeOptions, advisorsData } = useGetBusinessProfile({ enabled: true })
  // Fetch the messages with pagination support
  const {
    data: messageHistoryData,
    fetchNextPage,
    hasNextPage,
    isLoading: isMessagesLoading,
    isFetchingNextPage,
    isFetching,
    refetch: refetchMessages,
  } = useInfiniteGetMessagesFromBotQuery(
    {
      sessionId: sessionId ?? '',
    },
    {
      getNextPageParam: (lastPage) => {
        return lastPage.getMessagesFromBot.nextCursor
          ? { cursor: lastPage.getMessagesFromBot.nextCursor }
          : undefined
      },
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
    }
  )
  const { removeFlashParams } = useFlashParams<{
    connectorName: string
    toolId: string
  }>()

  // Add useForm hook
  const { handleSubmit, setValue, resetField } = useForm()

  // Text-to-speech hook
  const {
    isSupported: isVoiceSupported,
    isPlaying: isVoicePlaying,
    activeKey: activeVoiceMessageKey,
    duration: activeVoiceDuration,
    togglePlayback: toggleVoicePlayback,
  } = useVoicePlayback()

  const scrollToBottom = useCallback(
    (force?: boolean) => {
      if (!chatContainerRef.current) return
      if (!isUserScrolling || force) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
      }
    },
    [isUserScrolling]
  )

  // Check audio support on mount
  useEffect(() => {
    setAudioSupported(isAudioRecordingSupported())
  }, [])

  // Handle selected goal template from modal
  useEffect(() => {
    if (selectedGoalTemplate) {
      setInputValue(selectedGoalTemplate)
      setDisableButton(false)
      scrollToBottom(true)
      // Clear the selected template after using it
      onGoalTemplateUsed?.()
    }
  }, [selectedGoalTemplate, onGoalTemplateUsed, scrollToBottom])

  // Callback to handle goal template selection from within the chat
  const handleGoalTemplateSelected = useCallback(
    (goalText: string) => {
      setInputValue(goalText)
      setDisableButton(false)
      scrollToBottom(true)
    },
    [scrollToBottom]
  )

  const convertMessage = (message: {
    __typename?: 'ChatBotMessage'
    role: string
    content: string
    createdAt: string
    isEdited?: boolean | null
  }) => {
    if (message?.content?.startsWith('{')) {
      const content = JSON.parse(message.content)
      return {
        sender: message.role ?? '',
        content,
        createdAt: message.createdAt ?? '',
        isEdited: message.isEdited || false,
      }
    }
    return {
      sender: message.role ?? '',
      text: message.content ?? '',
      createdAt: message.createdAt ?? '',
      isEdited: message.isEdited || false,
    }
  }

  const isExistingMessage = (message) => {
    const isExisting = messages.find(
      (msg) =>
        msg.createdAt === message.createdAt &&
        (msg.text === message.text ||
          JSON.stringify(msg.content) === JSON.stringify(message.content))
    )
    return !isExisting
  }
  const prevSessionIdRef = useRef<string | undefined>()
  useEffect(() => {
    if (prevSessionIdRef.current !== sessionId) {
      // If the sessionId has changed, reset messages and refetch
      setMessages([]) // Clear previous messages
      prevSessionIdRef.current = sessionId
    }
  }, [sessionId])

  const addMessagesFromLatestPage = useCallback(
    (
      pageMessages: {
        __typename?: 'ChatBotMessage'
        role: string
        content: string
        createdAt: string
        isEdited?: boolean | null
      }[]
    ) => {
      const newMessages = pageMessages
        .map((message) => convertMessage(message))
        .reverse()
        .filter(Boolean)
      // Avoid adding duplicate messages by checking their createdAt and content
      setMessages((prevMessagesArray) => {
        const existingMessages = new Set(
          prevMessagesArray.map(
            (msg) => `${msg.createdAt}-${msg.text ?? JSON.stringify(msg.content)}`
          )
        )
        const oldMessages =
          newMessages?.filter((msg) => {
            const key = `${msg?.createdAt}-${msg?.text ?? JSON.stringify(msg?.content)}`
            return !existingMessages.has(key)
          }) ?? []
        return [...oldMessages, ...prevMessagesArray]
      })
    },
    []
  )

  useEffect(() => {
    if (!messageHistoryData || isRetrying) return

    const pageMessages = messageHistoryData.pages ?? []
    const lastPageMessages =
      pageMessages[pageMessages.length - 1]?.getMessagesFromBot.messages ?? []

    if (!messages.length && pageMessages.length > 1) {
      pageMessages.forEach((page) => {
        addMessagesFromLatestPage(page.getMessagesFromBot.messages ?? [])
      })
      return
    }
    if (lastPageMessages.length) {
      addMessagesFromLatestPage(lastPageMessages)
      if (progress === 0) {
        setProgress(lastPageMessages[0].progress ?? 0)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageHistoryData, sessionId, addMessagesFromLatestPage, messages.length])
  const {
    mutate: sendMessageToBot,
    data: latestResponse,
    isLoading: isSendingMessageTobot,
  } = useSendMessageToBotMutation({
    onError: async (error) => {
      if (error?.message === 'Execution timed out.') {
        const newMessage = 'There was a delay in processing. Please wait a moment...'
        addMessage({ sender: 'assistant', text: newMessage })
        setProcessing(true)
        scrollToBottom(true)
        setIsRetrying(true)

        // Try to fetch new messages for up to 30 seconds
        const intervalId = setInterval(async () => {
          const result = await refetchMessages({
            refetchPage: (_, index) => index === 0,
          })

          if (!result.data) return

          const lastMessage = result.data.pages[0]?.getMessagesFromBot?.messages?.[0]
          const lastCurrentMessage = messages[messages.length - 1]
          // If we found a new message from the bot
          if (
            lastMessage?.role === 'assistant' &&
            lastMessage?.createdAt !== lastCurrentMessage?.createdAt
          ) {
            setProcessing(false)
            const lastPageMessages = result.data.pages[0]?.getMessagesFromBot?.messages ?? []
            const differentMessages = lastPageMessages.filter(
              (message) =>
                !messages.find((m) => m.createdAt === message.createdAt) &&
                message.role === 'assistant'
            )
            // Add the new messages using the existing function
            const convertedMessages = differentMessages
              .map((message) => convertMessage(message))
              .filter((message) => isExistingMessage(message))
              .reverse()
            setMessages((prevMessages) => [...prevMessages, ...convertedMessages])
            const lastMessage = convertedMessages[0]
            if (lastMessage?.content?.type !== OnboardingFlowStepTypes.Message) {
              clearInterval(intervalId)
              setIsRetrying(false)
            }
            scrollToBottom(true)
          }
        }, 5000)
      }
    },
  })

  // Handle audio recording completion
  const handleAudioRecording = useCallback(
    async (audioBlob: Blob, transcript?: string, duration?: number) => {
      if (!audioBlob || processing || disableButton) return

      // Check if we have a transcript
      if (!transcript || transcript.trim() === '') {
        // Show user-friendly message and allow manual input
        const userInput = prompt(errorMessages.speechRecognitionFailed)
        if (!userInput || userInput.trim() === '') {
          return
        }
        transcript = userInput.trim()
      }

      try {
        // Determine if this should be treated as form input
        const lastMessageContent = messages[messages.length - 1]?.content
        const isFormContext = lastMessageContent?.type === OnboardingFlowStepTypes.Form

        const messageType = isFormContext
          ? ChatBotMessageTypes.AudioToForm
          : ChatBotMessageTypes.Audio

        // Add user message to UI immediately with voice metadata
        const userMessage = {
          sender: 'user',
          text: transcript.trim(),
          createdAt: new Date().toISOString(),
          isVoiceMessage: true,
          voiceDuration: duration || 0,
        }
        setMessages((prev) => [...prev, userMessage])
        setProcessing(true)

        // Send message using existing mutation
        sendMessageToBot(
          {
            input: {
              userMessage: transcript.trim(),
              userMessageType: messageType,
              audioTranscript: transcript.trim(),
              sessionId,
              isVoiceMessage: true,
              voiceDuration: duration || 0,
            },
          },
          {
            onError: (error) => {
              if (error.statusCode === 401) {
                window.location.reload()
              }
            },
          }
        )
      } catch (error) {
        console.error('Error processing audio:', error)
        setProcessing(false)
      }
    },
    [messages, processing, disableButton, sessionId, sendMessageToBot]
  )

  const handleNewMessage = (message: OnboardingStepMessageResponse) => {
    setCurrentStep(message)
    if ((message?.content as TextComponent)?.text?.toLowerCase().includes('goal center')) {
      refetchBusinessGoal()
    }
    const newMessage = { sender: 'assistant', content: message.content }
    setMessages((prevMessages) => [...prevMessages, newMessage])
    setIsUserScrolling(false)
    setProcessing(false)
  }

  useEffect(() => {
    if (latestResponse?.sendMessageToBot.responses?.length && !isRetrying) {
      const firstResponse = JSON.parse(
        latestResponse?.sendMessageToBot.responses[0]
      ) as OnboardingStepMessageResponse
      handleNewMessage(firstResponse)
      setQueuedMessages((prevMessages) => {
        const newMessages = latestResponse?.sendMessageToBot
          .responses!.slice(1)
          .map((response) => JSON.parse(response) as OnboardingStepMessageResponse)
        return [...prevMessages, ...newMessages]
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestResponse?.sendMessageToBot.responses])

  useEffect(() => {
    if (latestResponse?.sendMessageToBot.progress) {
      setProgress(latestResponse?.sendMessageToBot.progress)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestResponse?.sendMessageToBot.progress])

  useEffect(() => {
    const lastMessage = messages[messages.length - 1]
    const lastAssistantMessage = findLast(messages, (message) => message.sender === 'assistant')
    if (lastMessage?.sender === 'user') {
      scrollToBottom(true)
    }
    if (lastAssistantMessage?.content?.type === OnboardingFlowStepTypes.End) {
      const tenantId = getTenantId()
      const role = getRoleFromPath()
      const isAdvisor = user?.roles.includes('BSO_ADVISOR')
      if (isAdvisor) {
        redirectTo(`/${role}/${tenantId}/clients/${clientId ? clientId : user.id}/goals`)
      } else {
        redirectTo(`/owner-v2/${tenantId}/goals`)
      }
    }
    if (lastAssistantMessage?.content?.type === OnboardingFlowStepTypes.ToolConnection) {
      const content = lastAssistantMessage.content as { tools: Tool[] }
      const toolConnections = content.tools
      const connectedTools = toolConnections.filter((tool) =>
        tools.find((t) => t.id === tool.id && t.isConnected)
      )
      if (toolConnections.length === connectedTools.length && !isSendingMessageTobot) {
        setProcessing(true)
        sendMessageToBot(
          {
            input: {
              userMessage: JSON.stringify({ toolIds: connectedTools.map((tool) => tool.id) }),
              userMessageType: ChatBotMessageTypes.ToolConnected,
              sessionId,
            },
          },
          {
            onError: (error) => {
              if (error.statusCode === 401) {
                window.location.reload()
              }
            },
          }
        )
        removeFlashParams(['toolId'])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, tools])

  const submitDataConfirmationForm = async (formData) => {
    const filesFields = ['businessPlanFile']
    const filesToUpload = filesFields.filter((field) => formData[field])

    if (filesToUpload.length > 0) {
      filesToUpload.forEach((field) => {
        // Check if formData[field] is an array
        const files = Array.isArray(formData[field]) ? formData[field] : [formData[field]]
        uploadFileAction({
          files: files as unknown as FileList,
          isMultiple: files.length > 1, // Set isMultiple based on file count
          saveFile: true,
        })
      })
      return
    }
    setProcessing(true)
    const { id, ...data } = formData
    sendMessageToBot(
      {
        input: {
          userMessage: JSON.stringify(data),
          userMessageType: ChatBotMessageTypes.FormResponse,
          ...(formData.skip ? { skip: true } : {}),
          sessionId,
        },
      },
      {
        onError: (error) => {
          if (error.statusCode === 401) {
            window.location.reload()
          }
        },
      }
    )
  }

  const handleSendMessage = useCallback(async () => {
    const pageMessages = messageHistoryData?.pages ?? []
    const page = pageMessages.length
      ? pageMessages[pageMessages.length - 1]
      : { getMessagesFromBot: { messages: [] } }
    const lastPageMessages = page.getMessagesFromBot.messages ?? []
    if (!inputValue && lastPageMessages.length) return
    setProcessing(true)
    const userMessage = { sender: 'user', text: inputValue }
    addMessage(userMessage)
    setInputValue('')
    sendMessageToBot(
      {
        input: {
          userMessage: inputValue ?? '',
          sessionId,
          userMessageType: ChatBotMessageTypes.Text,
        },
      },
      {
        onError: (error) => {
          if (error.statusCode === 401) {
            window.location.reload()
          }
        },
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, messages])

  useEffect(() => {
    if (isMessagesLoading || isFetchingNextPage || isFetching) {
      return
    }
    const pageMessages = messageHistoryData?.pages ?? []
    const lastPageMessages =
      pageMessages[pageMessages.length - 1]?.getMessagesFromBot.messages ?? []
    if (messages.length || lastPageMessages.length) {
      return
    }
    if (!messages.length && !lastPageMessages.length) {
      handleSendMessage()
    }
  }, [
    handleSendMessage,
    isFetching,
    isFetchingNextPage,
    isMessagesLoading,
    messageHistoryData?.pages,
    messages.length,
  ])

  useEffect(() => {
    if (!uploadFileLoading && uploadedFileId && uploadedFileId.length > 0) {
      submitDataConfirmationForm({ businessPlan: uploadedFileId })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadFileLoading, uploadedFileId])

  useEffect(() => {
    if (uploadedFile && uploadedFile.length > 0 && startUpload) {
      setValue('businessPlanFile', uploadedFile.length === 1 ? uploadedFile[0] : uploadedFile)
      handleSubmit((formData) => submitDataConfirmationForm({ ...formData, skip: 'true' }))()
      setUploadedFile([])
      setStartUpload(false)
      resetField('businessPlanFile')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startUpload])

  const scrollTopMessage = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollTop + 250
    }
  }

  const handleUserScroll = () => {
    if (chatContainerRef.current) {
      const isAtBottom =
        chatContainerRef.current.scrollHeight - chatContainerRef.current.scrollTop ===
        chatContainerRef.current.clientHeight
      setIsUserScrolling(!isAtBottom)

      // Trigger loading more messages when the user scrolls to the top
      if (chatContainerRef.current.scrollTop === 0 && hasNextPage) {
        fetchNextPage()
      }
    }
  }

  const handleTypingMessage = (text: string, displayText: string) => {
    if (text === displayText) {
      setDisableButton(false)
      if (queuedMessages.length && text) {
        const firstQueueMessage = queuedMessages.shift()
        handleNewMessage(firstQueueMessage!)
        setQueuedMessages(queuedMessages)
        return
      }
    } else {
      if (text?.length) {
        setDisableButton(true)
      }
    }
    if (displayText.length) {
      scrollToBottom()
    } else {
      scrollTopMessage()
    }
  }

  const addMessage = (newMessage) => {
    setMessages((prevMessages) => [...prevMessages, newMessage])
    setIsUserScrolling(false)
  }

  const handleGoalTemplateSubmit = (goal: ISmartGoal) => {
    // Add user message to UI before sending to bot
    const goalMessageText = goal.goalName || ''
    const userMessage = {
      sender: 'user',
      text: goalMessageText,
      createdAt: new Date().toISOString(),
    }
    addMessage(userMessage)
    setProcessing(true)
    scrollToBottom(true)

    sendMessageToBot(
      {
        input: {
          userMessage: JSON.stringify({
            goalName: goal.goalName || '',
            smartHeading: goal.smartHeading || '',
            category: goal.category || '',
          }),
          sessionId: sessionId ?? '',
          userMessageType: ChatBotMessageTypes.Text,
        },
      },
      {
        onError: (error) => {
          setProcessing(false)
          if (error.statusCode === 401) {
            window.location.reload()
          }
        },
      }
    )
  }

  /**
   * Finds the prompting assistant message for a user message being edited.
   * Returns the index and metadata about the prompting assistant.
   */
  const findPromptingAssistant = (
    originalMessageIndex: number,
    editingStepType: OnboardingFlowStepTypes | null,
    editingStepContent: UIComponentProps | null
  ): {
    promptingAssistantIndex: number | undefined
    promptingStepType: OnboardingFlowStepTypes | null | undefined
    promptingStepContent: UIComponentProps | null | undefined
  } => {
    let promptingAssistantIndex: number | undefined
    let promptingStepType: OnboardingFlowStepTypes | null | undefined
    let promptingStepContent: UIComponentProps | null | undefined

    // Search backwards from the user message to find the prompting assistant
    for (let idx = originalMessageIndex - 1; idx >= 0; idx--) {
      const msg = messages[idx]

      // Stop if we hit another user message
      if (msg.sender === 'user' && !msg.isEditingMessage) {
        break
      }

      // For Question type, accept any assistant message
      if (editingStepType === OnboardingFlowStepTypes.Question) {
        if (msg.sender === 'assistant' && !msg.isEditingMessage) {
          promptingAssistantIndex = idx
          promptingStepType = editingStepType
          promptingStepContent = editingStepContent
          break
        }
      } else if (editingStepType) {
        // For other types (Form, GoalTemplate), match by content type
        if (
          msg.sender === 'assistant' &&
          !msg.isEditingMessage &&
          msg.content?.type === editingStepType
        ) {
          promptingAssistantIndex = idx
          promptingStepType = editingStepType
          promptingStepContent = editingStepContent
          break
        }
      }
    }

    if (promptingAssistantIndex === undefined) {
    }

    return { promptingAssistantIndex, promptingStepType, promptingStepContent }
  }

  const handleEditLastMessage = () => {
    // Find the last real user message (not editing messages)
    const lastUserMessage = findLast(
      messages,
      (msg) => msg.sender === 'user' && !msg.isEditingMessage
    )
    if (lastUserMessage) {
      // Find the assistant message that prompted the last user message
      const lastUserMessageIndex = findLastIndex(
        messages,
        (msg) => msg.sender === 'user' && !msg.isEdited && !msg.isEditingMessage
      )

      let stepType: OnboardingFlowStepTypes | null = null
      let stepContent: UIComponentProps | null = null
      let messageText = lastUserMessage.text || ''
      let promptingAssistantIndex: number | undefined

      // Check if this message has stored original prompting information (from previous edits)
      if (lastUserMessage.originalPromptingAssistantIndex !== undefined) {
        // Use the stored original prompting assistant message
        promptingAssistantIndex = lastUserMessage.originalPromptingAssistantIndex
        const promptingAssistantMessage = messages[promptingAssistantIndex]

        if (promptingAssistantMessage && promptingAssistantMessage.content?.type) {
          stepType = promptingAssistantMessage.content.type as OnboardingFlowStepTypes

          if (
            stepType === OnboardingFlowStepTypes.Form ||
            stepType === OnboardingFlowStepTypes.GoalTemplate
          ) {
            // For form steps, we need to re-show the form
            stepContent = (promptingAssistantMessage.content as FormComponent)
              ? ({
                  ...promptingAssistantMessage.content,
                  status: OnboardingFlowStepStatus.InProgress,
                } as FormComponent)
              : null
          } else {
            // For question/text steps, show text input
            stepType = OnboardingFlowStepTypes.Question
            stepContent = null
          }
        } else if (lastUserMessage.originalPromptingStepType) {
          // Fallback to stored step type and content
          stepType = lastUserMessage.originalPromptingStepType
          stepContent = lastUserMessage.originalPromptingStepContent || null
        }
      } else {
        // First time editing - find the assistant message immediately before the last user message
        // Make sure we're looking BEFORE the user message, not after, and skip editing messages
        promptingAssistantIndex = undefined

        // Search backwards from the user message to find the immediate preceding assistant message
        // Stop if we encounter another user message (which would mean we've gone too far back)
        for (let idx = lastUserMessageIndex - 1; idx >= 0; idx--) {
          const msg = messages[idx]

          // If we hit another user message, stop searching - we've gone too far back
          if (msg.sender === 'user' && !msg.isEditingMessage) {
            break
          }

          // Skip editing messages and only look for real assistant messages
          if (msg.sender === 'assistant' && !msg.isEditingMessage) {
            promptingAssistantIndex = idx
            break
          }
        }

        if (promptingAssistantIndex !== undefined) {
          const promptingAssistantMessage = messages[promptingAssistantIndex]

          // Get the step type from the assistant message that prompted the user's response
          if (promptingAssistantMessage && promptingAssistantMessage.content?.type) {
            stepType = promptingAssistantMessage.content.type as OnboardingFlowStepTypes

            if (
              stepType === OnboardingFlowStepTypes.Form ||
              stepType === OnboardingFlowStepTypes.GoalTemplate
            ) {
              // For form steps, we need to re-show the form
              stepContent = (promptingAssistantMessage.content as FormComponent)
                ? ({
                    ...promptingAssistantMessage.content,
                    status: OnboardingFlowStepStatus.InProgress,
                  } as FormComponent)
                : null
            } else {
              // For question/text steps, show text input
              stepType = OnboardingFlowStepTypes.Question
              stepContent = null
            }
          } else {
            // Default to text input if we can't determine the step type
            stepType = OnboardingFlowStepTypes.Question
            stepContent = null
          }
        } else {
          // Default to text input if we can't find a prompting message
          stepType = OnboardingFlowStepTypes.Question
          stepContent = null
        }
      }

      // Pre-fill only the editing textarea (not the main input)
      setEditingTextareaValue(messageText)
      setInputValue('') // Keep main input empty

      // Add editing message as a bot message
      const editingMessage = {
        sender: 'assistant',
        text: messageText,
        isEditingMessage: true,
        editingOriginalMessageIndex: lastUserMessageIndex,
        editingStepType: stepType,
        editingStepContent: stepContent,
        editingMessageText: messageText,
        createdAt: new Date().toISOString(),
      }

      setMessages((prev) => [...prev, editingMessage])
      scrollToBottom(true)
    }
  }

  const handleCancelEdit = () => {
    // Remove the editing message from the messages array
    setMessages((prev) => prev.filter((msg) => !msg.isEditingMessage))
    setEditingTextareaValue('')
    setInputValue('')
  }

  const handleSaveEdit = () => {
    const trimmedText = (editingTextareaValue || '').trim()
    if (!trimmedText) {
      handleCancelEdit()
      return
    }

    // Find the editing message
    const editingMessageIndex = messages.findIndex((msg) => msg.isEditingMessage)
    if (editingMessageIndex === -1) return

    const editingMessage = messages[editingMessageIndex]
    const originalMessageIndex = editingMessage.editingOriginalMessageIndex

    if (originalMessageIndex === undefined) return

    // Get the original message to preserve its prompting assistant info
    const originalMessage = messages[originalMessageIndex]

    // Determine the prompting assistant index - use stored value or calculate from editing message
    let promptingAssistantIndex: number | undefined =
      originalMessage?.originalPromptingAssistantIndex
    let promptingStepType: OnboardingFlowStepTypes | null | undefined =
      originalMessage?.originalPromptingStepType
    let promptingStepContent: UIComponentProps | null | undefined =
      originalMessage?.originalPromptingStepContent

    // If not stored, find the prompting assistant message
    if (promptingAssistantIndex === undefined && editingMessage.editingStepType) {
      const result = findPromptingAssistant(
        originalMessageIndex,
        editingMessage.editingStepType,
        editingMessage.editingStepContent || null
      )
      promptingAssistantIndex = result.promptingAssistantIndex
      promptingStepType = result.promptingStepType
      promptingStepContent = result.promptingStepContent
    }

    // Keep entire history, mark original as edited, and append the new message
    setMessages((prev) => {
      const filteredMessages = prev.filter((msg) => !msg.isEditingMessage)
      const isLastMessage = originalMessageIndex === filteredMessages.length - 1

      const updatedMessage = {
        sender: 'user' as const,
        text: trimmedText,
        originalPromptingAssistantIndex: promptingAssistantIndex,
        originalPromptingStepType: promptingStepType,
        originalPromptingStepContent: promptingStepContent,
      }

      if (isLastMessage) {
        // If editing the last message, replace it
        return filteredMessages.map((msg, idx) =>
          idx === originalMessageIndex ? { ...msg, isEdited: true, ...updatedMessage } : msg
        )
      }

      // Otherwise, keep history and append
      const updatedHistory = filteredMessages.map((msg, idx) =>
        idx === originalMessageIndex ? { ...msg, isEdited: true } : msg
      )
      return [...updatedHistory, updatedMessage]
    })
    setIsUserScrolling(false)

    // Set processing state to show loading
    setProcessing(true)

    // Send edited message with the edit flag
    sendMessageToBot(
      {
        input: {
          userMessage: trimmedText,
          sessionId: sessionId ?? '',
          userMessageType: ChatBotMessageTypes.Text,
          isEditingLastMessage: true,
        },
      },
      {
        onSuccess: (response) => {
          // Reset inputs
          setEditingTextareaValue('')
          setInputValue('')
          setIsRetrying(false)
          // Note: Don't set processing to false here, let the normal response handler do it
        },
        onError: (error) => {
          setProcessing(false)
          console.error('Edit submission failed:', error)
        },
      }
    )
  }

  const handleEditFormSubmit = (formData: Record<string, string | string[] | Date | Date[]>) => {
    // Find the editing message
    const editingMessageIndex = messages.findIndex((msg) => msg.isEditingMessage)
    if (editingMessageIndex === -1) return

    const editingMessage = messages[editingMessageIndex]
    const originalMessageIndex = editingMessage.editingOriginalMessageIndex

    if (originalMessageIndex === undefined) return

    // Get the original message to preserve its prompting assistant info
    const originalMessage = messages[originalMessageIndex]

    // Determine the prompting assistant index - use stored value or calculate from editing message
    let promptingAssistantIndex: number | undefined =
      originalMessage?.originalPromptingAssistantIndex
    let promptingStepType: OnboardingFlowStepTypes | null | undefined =
      originalMessage?.originalPromptingStepType
    let promptingStepContent: UIComponentProps | null | undefined =
      originalMessage?.originalPromptingStepContent

    // If not stored, find the prompting assistant message
    if (promptingAssistantIndex === undefined && editingMessage.editingStepType) {
      const result = findPromptingAssistant(
        originalMessageIndex,
        editingMessage.editingStepType,
        editingMessage.editingStepContent || null
      )
      promptingAssistantIndex = result.promptingAssistantIndex
      promptingStepType = result.promptingStepType
      promptingStepContent = result.promptingStepContent
    }

    // Format the response (exclude 'id' field which contains the form title)
    const { id, ...dataWithoutId } = formData

    // Get form fields from the editing step content to format values properly
    const stepContent = editingMessage.editingStepContent as FormComponent | null
    const formFields = stepContent?.formFields || []

    // Format each field value based on its type
    const formattedValues: string[] = []
    Object.entries(dataWithoutId).forEach(([fieldName, fieldValue]) => {
      const formField = formFields.find((f) => f.name === fieldName)

      if (!formField || !fieldValue) {
        // If no field found or value is empty, skip or use value as-is
        if (fieldValue) {
          formattedValues.push(String(fieldValue))
        }
        return
      }

      // Handle date fields - format as MM/DD/YYYY
      if (formField.fieldType === 'date') {
        try {
          // Handle array of dates (for multiple date selection)
          if (Array.isArray(fieldValue)) {
            const formattedDates = fieldValue
              .map((date) => {
                const dateValue = moment(date)
                return dateValue.isValid() ? dateValue.format('MM/DD/YYYY') : String(date)
              })
              .filter(Boolean)
            if (formattedDates.length > 0) {
              formattedValues.push(formattedDates.join(', '))
            }
          } else {
            // Handle single date (could be Date object, string, or ISO string)
            const dateValue = moment(fieldValue instanceof Date ? fieldValue : fieldValue)
            if (dateValue.isValid()) {
              formattedValues.push(dateValue.format('MM/DD/YYYY'))
            } else {
              formattedValues.push(String(fieldValue))
            }
          }
        } catch {
          formattedValues.push(String(fieldValue))
        }
      }
      // Handle checkbox and select fields - map values to labels
      else if (
        formField.fieldType === 'checkbox' ||
        formField.fieldType === 'select' ||
        formField.fieldType === 'buttonSelect'
      ) {
        if (formField.options) {
          // Handle array values (for multiple selections)
          const values = Array.isArray(fieldValue) ? fieldValue : [fieldValue]
          const labels = values
            .map((val) => {
              // Convert value to string for comparison (handles Date objects, etc.)
              const stringVal = val instanceof Date ? moment(val).format('MM/DD/YYYY') : String(val)
              const option = formField.options?.find(
                (opt) => opt.value === stringVal || opt.value === val
              )
              return option?.label || stringVal
            })
            .filter(Boolean)
          if (labels.length > 0) {
            formattedValues.push(labels.join(', '))
          }
        } else {
          formattedValues.push(String(fieldValue))
        }
      }
      // For other field types, use value as-is
      else {
        formattedValues.push(String(fieldValue))
      }
    })

    const formattedResponse = formattedValues.join(', ')

    // For goal template edits, only display the goal name (not category/smartHeading)
    const isGoalTemplateEdit =
      editingMessage.editingStepType === OnboardingFlowStepTypes.GoalTemplate
    const displayText = isGoalTemplateEdit
      ? (formData.goalName as string) || formattedResponse
      : formattedResponse

    // Keep entire history, mark original as edited, and append the new message
    setMessages((prevMessages) => {
      const filteredMessages = prevMessages.filter((msg) => !msg.isEditingMessage)
      const isLastMessage = originalMessageIndex === filteredMessages.length - 1

      const updatedMessage = {
        sender: 'user' as const,
        text: displayText,
        originalPromptingAssistantIndex: promptingAssistantIndex,
        originalPromptingStepType: promptingStepType,
        originalPromptingStepContent: promptingStepContent,
      }

      if (isLastMessage) {
        // If editing the last message, replace it
        return filteredMessages.map((msg, idx) =>
          idx === originalMessageIndex ? { ...msg, isEdited: true, ...updatedMessage } : msg
        )
      }

      // Otherwise, keep history and append
      const updatedHistory = filteredMessages.map((msg, idx) =>
        idx === originalMessageIndex ? { ...msg, isEdited: true } : msg
      )
      return [...updatedHistory, updatedMessage]
    })
    setIsUserScrolling(false)

    // Set processing state to show loading
    setProcessing(true)

    sendMessageToBot(
      {
        input: {
          userMessage: JSON.stringify(formData),
          sessionId: sessionId ?? '',
          userMessageType: isGoalTemplateEdit
            ? ChatBotMessageTypes.Text
            : ChatBotMessageTypes.FormResponse,
          isEditingLastMessage: true,
        },
      },
      {
        onSuccess: (response) => {
          setIsRetrying(false)
          // Note: Don't set processing to false here, let the normal response handler do it
        },
        onError: (error) => {
          setProcessing(false)
          console.error('Edit form submission failed:', error)
        },
      }
    )
    scrollToBottom()
  }

  // Note: Chat context generation function removed - play descriptions are now empty by default

  // Wrapper function to handle the UserTask type difference
  const handleSetCreatedUserTask: React.Dispatch<React.SetStateAction<UserTask>> = (value) => {
    if (typeof value === 'function') {
      setCreatedUserTask((prev) => value(prev!))
    } else {
      setCreatedUserTask(value)
      // If a UserTask is created successfully, disable the play button
      if (value) {
        setIsPlayCreated(true)
      }
    }
  }

  const getDisplayText = (text: string | undefined, currentIndex: number): string => {
    if (!text) return ''

    // Find the closest previous assistant form message to map values to labels and format dates
    const previousFormMessage = findLast(messages.slice(0, currentIndex), (msg) => {
      if (msg.sender !== 'assistant' || !msg.content || typeof msg.content !== 'object')
        return false
      const previousContent = msg.content as FormComponent
      return previousContent?.formFields && previousContent.formFields.length > 0
    })

    if (!previousFormMessage || typeof previousFormMessage.content !== 'object') {
      // If no form found, try to format dates in the text (for standalone date values)
      return text
        .split(',')
        .map((value) => {
          const trimmedValue = value.trim()
          // Check if it looks like an ISO date string
          if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(trimmedValue)) {
            const dateValue = moment(trimmedValue)
            if (dateValue.isValid()) {
              return dateValue.format('MM/DD/YYYY')
            }
          }
          return trimmedValue
        })
        .join(', ')
    }

    const previousFormContent = previousFormMessage.content as FormComponent
    const formFields = previousFormContent?.formFields || []

    // Build maps for option labels
    const optionLabelMap =
      formFields.reduce<Record<string, string>>((acc, field) => {
        if (field.options?.length) {
          field.options.forEach((opt) => {
            acc[opt.value] = opt.label
          })
        }
        return acc
      }, {}) ?? {}

    // Split by comma to handle multiple selections, trim spaces, and format values
    return text
      .split(',')
      .map((value) => {
        const trimmedValue = value.trim()

        // First, try to map to option labels (for checkbox/select fields)
        if (optionLabelMap[trimmedValue]) {
          return optionLabelMap[trimmedValue]
        }

        // Then, check if it's a date (ISO format or other date formats)
        // Check if it looks like an ISO date string
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(trimmedValue)) {
          const dateValue = moment(trimmedValue)
          if (dateValue.isValid()) {
            return dateValue.format('MM/DD/YYYY')
          }
        }

        // Check if it's a valid date in other formats
        const dateValue = moment(trimmedValue)
        if (dateValue.isValid() && trimmedValue.length > 5) {
          // Only format if it's a reasonable date string (not just numbers)
          return dateValue.format('MM/DD/YYYY')
        }

        return trimmedValue
      })
      .join(', ')
  }

  const lastMessageContent = messages[messages.length - 1]?.content
  const isFormStep =
    currentStep.stepType === OnboardingFlowStepTypes.Form ||
    (lastMessageContent?.type && lastMessageContent.type === OnboardingFlowStepTypes.Form)
  const isToolConnectionStep =
    currentStep.stepType === OnboardingFlowStepTypes.ToolConnection ||
    (lastMessageContent?.type && lastMessageContent.type === OnboardingFlowStepTypes.ToolConnection)
  return (
    <div className="flex flex-col gap-4 w-full rounded-lg h-5/6 bg-app-grey-5">
      <div className="p-2 sm:p-4 pb-0 bg-app-grey-5">
        <div className="flex justify-between items-center w-full">
          <div className="flex-1" />
          <div className="w-full bg-app-grey-5 font-bold font-inter text-center text-sm">
            {flowType
              ? flowType === OnboardingFlowType.GetToKnowYou
                ? "Let's Get To Know You!"
                : "Let's Get Started Tackling Your Goals!"
              : 'Coach Logic entering the chat...'}
          </div>
          <div className="flex-1" />
        </div>
        <div className="w-full bg-app-grey-5 font-normal font-inter text-center text-sm text-grey-lighter6x">
          {`Today, ${moment().format('hh:mm A')}`}
        </div>
      </div>
      <div
        className="flex flex-col w-full pt-0 overflow-y-scroll h-[600px] gap-8"
        ref={chatContainerRef}
        onScroll={handleUserScroll}
      >
        {isFetchingNextPage && (
          <div className="flex justify-center p-1">
            <LoadingIndicator size={20} />
          </div>
        )}
        {messages.map((message, index) => {
          const isLastMessage = index === messages.length - 1
          const lastUserMessageIndex = findLastIndex(messages, (msg) => msg.sender === 'user')
          const isEditingLastMessage = index === lastUserMessageIndex
          // Check if current message is edited (not the next one)
          const isEdited = message.isEdited || false
          const messageKey = message.createdAt ?? `message-${index}`
          const assistantVoiceText =
            message.sender === 'assistant' && typeof message.text === 'string'
              ? extractTextForTTS(message.text)
              : ''
          const assistantContentTranscript =
            message.sender === 'assistant' &&
            message.content &&
            typeof message.content === 'object' &&
            'transcript' in message.content &&
            typeof (message.content as FormComponent).transcript === 'string'
              ? (message.content as FormComponent).transcript
              : undefined
          const assistantContentVoiceText =
            assistantContentTranscript ||
            (message.sender === 'assistant' && message.content
              ? extractTextForTTS(message.content)
              : '')
          const showVoicePlayback = isVoiceSupported && !!assistantVoiceText
          const showContentVoicePlayback = isVoiceSupported && !!assistantContentVoiceText
          const isVoiceActive = activeVoiceMessageKey === messageKey

          // Check if this is an editing message
          if (message.isEditingMessage) {
            console.log('Rendering editing message:', {
              index,
              editingStepType: message.editingStepType,
              hasEditingStepContent: !!message.editingStepContent,
              editingStepContentType: message.editingStepContent?.type,
              editingOriginalMessageIndex: message.editingOriginalMessageIndex,
            })

            return (
              <React.Fragment key={messageKey}>
                <div className="flex gap-2 justify-start">
                  <div>
                    <AppAvatar
                      size={'md'}
                      type="image"
                      shape={'Rounded'}
                      text={userInitial}
                      color="Primary"
                      src={Avatar}
                    />
                  </div>
                  <div className="flex-1 max-w-[90%]">
                    {/* Header with pencil icon, "Editing message" text, and X icon */}
                    <div className="flex justify-between items-center mb-3 p-3 bg-app-grey-20 rounded-lg">
                      <div className="flex items-center gap-2">
                        <PenIcon className="w-5 h-5" />
                        <span className="text-sm font-medium text-app-grey-100">
                          Editing message
                        </span>
                      </div>
                      <button
                        onClick={handleCancelEdit}
                        className="p-1 hover:bg-app-grey-20 rounded transition-colors"
                        aria-label="Cancel editing"
                      >
                        <AppCrossIcon className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Render form or text based on step type */}
                    {message.editingStepType === OnboardingFlowStepTypes.Question ? (
                      // For text messages, show textarea input with Save button
                      <div className="mb-4">
                        {/* Textarea input */}
                        <div className="flex flex-col rounded-lg px-4 py-3 border border-primary-appDarkBorder">
                          <textarea
                            rows={6}
                            value={editingTextareaValue}
                            onChange={(e) => setEditingTextareaValue(e.target.value)}
                            placeholder="Message Coach Logic..."
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
                                e.preventDefault()
                                handleSaveEdit()
                              } else if (e.key === 'Escape') {
                                e.preventDefault()
                                handleCancelEdit()
                              }
                            }}
                            className="w-full resize-none font-inter text-base text-black-appDark leading-6 bg-transparent border-none focus-visible:ring-0 focus-visible:outline-none min-h-[120px]"
                          />
                          <div className="flex justify-end mt-2">
                            <AppButton
                              variant="primary"
                              size="sm"
                              label="Save"
                              disabled={!editingTextareaValue?.trim() || processing}
                              onClick={handleSaveEdit}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (message.editingStepType === OnboardingFlowStepTypes.Form ||
                        message.editingStepType === OnboardingFlowStepTypes.GoalTemplate) &&
                      message.editingStepContent ? (
                      // Render the form for editing
                      <div className="mb-4">
                        <AppDynamicChatMessage
                          content={message.editingStepContent}
                          onSubmit={(formData) => {
                            handleEditFormSubmit(formData)
                          }}
                          // addMessage={addMessage} // Removed to prevent duplicate messages on edit
                          typingAnimationCallback={() => {}}
                          useTypingAnimation={false}
                          isLastMessage={true}
                          isSendingMessageTobot={false}
                          tools={tools}
                          setIsUpload={setIsUpload}
                          showUploadModal={showUploadModal}
                          isUpload={isUpload}
                          uploadedFile={uploadedFile}
                          setUploadedFile={setUploadedFile}
                          startUpload={startUpload}
                          setStartUpload={setStartUpload}
                          scrollToBottom={scrollToBottom}
                          refetchBusinessGoal={refetchBusinessGoal}
                          refetchTool={refetch}
                          onGoalTemplateSelected={handleGoalTemplateSelected}
                        />
                      </div>
                    ) : null}

                    {/* Warning banner */}
                    <div className="p-3 bg-app-primary-10 border border-app-primary-25 rounded-lg text-sm text-app-primary-100">
                      <div className="flex items-start gap-2">
                        <span className="text-app-primary-125 font-bold">!</span>
                        <span>
                          Editing this message may update Coach Logic's subsequent responses
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            )
          }

          if (message.text) {
            // Check if there's an active editing message for this user message
            const hasActiveEditing = messages.some(
              (msg) => msg.isEditingMessage && msg.editingOriginalMessageIndex === index
            )

            return (
              <React.Fragment key={messageKey}>
                <div
                  className={`flex gap-2 ${
                    message.sender === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div className={`${message.sender === 'user' ? 'order-2' : ''}`}>
                    <AppAvatar
                      size={'md'}
                      type={!logo && message.sender === 'user' ? 'text' : 'image'}
                      shape={'Rounded'}
                      text={userInitial}
                      color="Primary"
                      src={message.sender === 'user' ? logo : Avatar}
                    />
                  </div>
                  {/* Voice message display for user voice messages */}
                  {message.sender === 'user' && message.isVoiceMessage ? (
                    <div className="max-w-[90%]">
                      <AppVoicePlayback
                        isActive={isVoiceActive}
                        isPlaying={isVoicePlaying && isVoiceActive}
                        duration={message.voiceDuration}
                        onToggle={() => toggleVoicePlayback(messageKey, message.text || '')}
                      />
                    </div>
                  ) : (
                    /* Regular message bubble with edit controls */
                    <div className="w-fit max-w-[90%] flex flex-col">
                      <div
                        className={`flex rounded-lg px-3 pt-2 pb-1.5 text-base text-black-appDark !font-inter ${
                          message.sender === 'assistant'
                            ? 'bg-background-appLight text-black'
                            : 'bg-primary-appBrand text-white'
                        }`}
                      >
                        {message.sender === 'assistant' && message.text && isLastMessage ? (
                          <TypingMessage text={message.text} onNewCharacter={handleTypingMessage} />
                        ) : message.sender === 'assistant' ? (
                          <SafeMarkdown className="chatbot-markdown">{message.text}</SafeMarkdown>
                        ) : (
                          <>{getDisplayText(message.text, index)}</>
                        )}
                      </div>
                      {/* Edit button and Edited message indicator directly under message bubble */}
                      {message.sender === 'user' && (
                        <div className="flex items-center gap-2 justify-end">
                          {isEditingLastMessage &&
                            !hasActiveEditing &&
                            !processing &&
                            !disableButton && (
                              <>
                                <button
                                  onClick={handleEditLastMessage}
                                  className="mt-3"
                                  title="Edit message"
                                >
                                  <PenIcon className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={async () => {
                                    if (message.text) {
                                      try {
                                        await copyToClipboard(message.text)
                                      } catch (err) {
                                        console.error('Failed to copy: ', err)
                                      }
                                    }
                                  }}
                                  className="mt-3"
                                  title="Copy message"
                                >
                                  <CopyIcon className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          {isEdited && (
                            <div className="flex items-center gap-1">
                              <div className="text-xs text-gray-400 italic mt-3">
                                Edited message
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {showVoicePlayback && (
                  <div className="flex justify-start ml-12">
                    <AppVoicePlayback
                      isActive={isVoiceActive}
                      isPlaying={isVoicePlaying}
                      duration={activeVoiceDuration}
                      onToggle={() => toggleVoicePlayback(messageKey, assistantVoiceText)}
                    />
                  </div>
                )}
              </React.Fragment>
            )
          }
          if (message.content) {
            return (
              <React.Fragment key={messageKey}>
                <div
                  className={`flex gap-2 ${
                    message.sender === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div className={`${message.sender === 'user' ? 'order-2' : ''}`}>
                    <AppAvatar
                      size={'md'}
                      type={!logo && message.sender === 'user' ? 'text' : 'image'}
                      shape={'Rounded'}
                      text={userInitial}
                      color="Primary"
                      src={message.sender === 'user' ? logo : Avatar}
                    />
                  </div>
                  <div className="flex-1">
                    <AppDynamicChatMessage
                      key={index}
                      content={{ ...message.content }}
                      onSubmit={submitDataConfirmationForm}
                      onGoalTemplateSubmit={handleGoalTemplateSubmit}
                      typingAnimationCallback={handleTypingMessage}
                      useTypingAnimation={index === messages.length - 1}
                      isLastMessage={isLastMessage}
                      isSendingMessageTobot={isSendingMessageTobot}
                      tools={tools}
                      setIsUpload={setIsUpload}
                      showUploadModal={showUploadModal}
                      isUpload={isUpload}
                      uploadedFile={uploadedFile}
                      setUploadedFile={setUploadedFile}
                      startUpload={startUpload}
                      setStartUpload={setStartUpload}
                      scrollToBottom={scrollToBottom}
                      refetchBusinessGoal={refetchBusinessGoal}
                      refetchTool={refetch}
                      addMessage={addMessage}
                      onGoalTemplateSelected={handleGoalTemplateSelected}
                    />
                  </div>
                  {message.sender === 'user' && message.isEdited && (
                    <div className="text-xs text-gray-400 mt-1 italic">(edited)</div>
                  )}
                </div>
                {showContentVoicePlayback && (
                  <div className="flex justify-start ml-12">
                    <AppVoicePlayback
                      isActive={isVoiceActive}
                      isPlaying={isVoicePlaying}
                      duration={activeVoiceDuration}
                      onToggle={() => toggleVoicePlayback(messageKey, assistantContentVoiceText)}
                    />
                  </div>
                )}
              </React.Fragment>
            )
          }
          return <></>
        })}
        <div className="text-center w-full bg-app-grey-5 font-normal font-inter text-xs text-primary-appBrand flex justify-center cursor-pointer">
          {!(processing || disableButton || !messages.length) && showGetHelpButton && (
            <div className="chatbot-help py-1 px-2 hover:bg-blue" onClick={helpDesk}>
              Get Help?
            </div>
          )}
        </div>
        {processing || isSendingMessageTobot ? <AppTypingIndicator /> : <></>}
      </div>
      <div className="p-4 sm:p-8 pt-0">
        {/* Check if there's an active editing message */}
        {(() => {
          const editingMessage = messages.find((msg) => msg.isEditingMessage)
          const isEditing = !!editingMessage

          return (
            <div className="flex flex-col rounded-lg px-4 py-3 border border-primary-appDarkBorder">
              <textarea
                disabled={
                  processing || disableButton || isFormStep || isToolConnectionStep || isEditing
                }
                rows={6}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Message Coach Logic..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
                    e.preventDefault()
                    if (isEditing) {
                      handleSaveEdit()
                    } else {
                      handleSendMessage()
                    }
                  } else if (
                    (e.key === 'Enter' && e.shiftKey) ||
                    (e.key === 'Enter' && e.ctrlKey)
                  ) {
                  } else if (e.key === 'Escape') {
                    e.preventDefault()
                    if (isEditing) {
                      handleCancelEdit()
                    } else {
                      setInputValue('')
                    }
                  } else if (e.key === 'Backspace' && e.ctrlKey) {
                    e.preventDefault()
                    setInputValue((prev) => {
                      // Delete the last word
                      const lastSpaceIndex = prev?.trimEnd().lastIndexOf(' ') ?? -1
                      return lastSpaceIndex !== -1
                        ? prev?.substring(0, lastSpaceIndex + 1) ?? ''
                        : ''
                    })
                  }
                }}
                className="w-full resize-none font-inter text-base text-black-appDark leading-6 bg-transparent border-none focus-visible:ring-0 focus-visible:outline-none min-h-[120px]"
              />
              <div className="flex justify-between relative w-full">
                <div className="flex gap-2 items-center">
                  <AppButton
                    variant="clear"
                    size="sm"
                    RightIcon={AppPaperClipIcon}
                    disabled={
                      !isUpload ||
                      processing ||
                      isSendingMessageTobot ||
                      startUpload ||
                      isToolConnectionStep
                    }
                    onClick={() => setShowUploadModal(!showUploadModal)}
                  />
                  {/* Audio Recorder */}
                  {audioSupported && (
                    <AppAudioRecorder
                      onRecordingComplete={handleAudioRecording}
                      disabled={processing || disableButton || isToolConnectionStep}
                      maxDuration={audioMaxDuration}
                      className="ml-1"
                    />
                  )}
                  {/* Play button - show for both "Let's Get To Know You!" and "Let's Get Started Tackling Your Goals!" flows */}
                  <AppToolTip name="create-play-tooltip" text="Create Play" variant="clear">
                    <AppButton
                      variant="clear"
                      size="sm"
                      RightIcon={AppPlayIcon}
                      disabled={processing || isSendingMessageTobot || isPlayCreated}
                      onClick={() => setShowCreatePlayModal(true)}
                    />
                  </AppToolTip>
                  {/* <AppVerticalDivider />
                <AppButton
                  onClick={() => setEmojiClicked(!emojiClicked)}
                  variant="transparent"
                  size="sm"
                  RightIcon={AppEmojiIcon}
                />
                <AppButton variant="transparent" size="sm" RightIcon={AppAtSymbol} /> */}
                </div>

                {/* {emojiClicked && !processing && !disableButton && (
                <EmojiPicker
                  style={{
                    position: 'absolute',
                    bottom: '40%',
                    zIndex: 99999,
                    height: '450px',
                    width: '350px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                  }}
                  reactionsDefaultOpen={false}
                  onReactionClick={(e) => setInputValue((prve) => (prve ? prve : '') + e.emoji)}
                  onEmojiClick={(e) => setInputValue((prve) => (prve ? prve : '') + e.emoji)}
                />
              )} */}

                {processing ? (
                  <CircleNotchIcon className={'fill-primary h-[30px]'} />
                ) : (
                  <AppButton
                    variant="primary"
                    size="sm"
                    RightIcon={AppSendMessageIcon}
                    disabled={processing || disableButton || inputValue?.trim() === ''}
                    onClick={() => {
                      if (isEditing) {
                        handleSaveEdit()
                      } else {
                        handleSendMessage()
                      }
                    }}
                  />
                )}
              </div>
            </div>
          )
        })()}
      </div>
      {showUploadModal && (
        <AppModalBox title={'Upload Files'} onClose={() => setShowUploadModal(false)}>
          <div className="m-10">
            <AppFileUploadField
              name="imageData"
              className="flex-1"
              labelFontSize="base"
              maxFiles={10} // Allow up to 5 files
              onChange={(files) => {
                setUploadedFile(files)
              }}
            />
            <div className="mt-20 w-[30%]">
              <AppButton
                variant="primary"
                size="sm"
                label="Upload"
                disabled={!uploadedFile || uploadedFile.length === 0}
                onClick={() => {
                  setStartUpload(true)
                  setShowUploadModal(false)
                }}
              />
            </div>
          </div>
        </AppModalBox>
      )}

      {/* Create Play Modal */}
      {showCreatePlayModal && (
        <AppModalBox
          Icon={AppCrossIcon}
          height="max-h-[95vh] min-h-[50vh] h-fit"
          width="w-[95vw] sm:w-[90vw] md:w-[85vw] lg:w-[75vw] xl:max-w-2xl mx-2 sm:mx-4 md:mx-auto"
          titleSize="sm"
          title="Create new play"
          showBorder={false}
          onClose={() => {
            clearUserTaskFormData()
            setShowCreatePlayModal(false)
          }}
          onBack={() => {
            clearUserTaskFormData()
            setShowCreatePlayModal(false)
          }}
        >
          <AppTask
            tenantId={getTenantId()}
            userId={user?.id || ''}
            source={UserTaskSource.General}
            setShowTaskModal={setShowCreatePlayModal}
            setShowTaskSuccessModal={setShowPlaySuccessModal}
            setShowTaskErrorModal={setShowPlayErrorModal}
            setCreatedUserTask={handleSetCreatedUserTask}
            refetch={() => {}} // No specific refetch needed for chat context
            logo={logo || ''}
            userName={`${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name || ''}
            advisorsData={advisorsData || []}
            userAvatar={user?.avatar || ''}
            userFirstName={user?.firstName || ''}
            userLastName={user?.lastName || ''}
            goalEndDate=""
            assigneeOptions={assigneeOptions}
            paramUserId={user?.id || ''}
            initialDescription="" // Empty description - no chat context
          />
        </AppModalBox>
      )}

      {/* Play Creation Success/Error Modal */}
      {(showPlaySuccessModal || showPlayErrorModal) && createdUserTask && (
        <AppTaskCreationConfirmation
          showAddTaskSuccessModal={showPlaySuccessModal}
          setShowAddTaskModal={setShowCreatePlayModal}
          setShowAddTaskSuccessModal={setShowPlaySuccessModal}
          setShowAddTaskErrorModal={setShowPlayErrorModal}
          createdUserTask={createdUserTask}
          userId={user?.id || ''}
          userAvatar={user?.avatar || ''}
          userFirstName={user?.firstName || ''}
          userLastName={user?.lastName || ''}
          assigneeOptions={assigneeOptions}
        />
      )}
    </div>
  )
}
